<template>
  <main class="container mx-auto flex flex-col items-center py-10 px-4">
    <div class="mb-2 w-full flex text-sm text-primary-500">
      <router-link to="/">&lt; トップページへ戻る</router-link>
    </div>
    <h1 class="text-2xl font-bold mb-10">
      <ruby><rb>今日</rb><rp>（</rp><rt>きょう</rt><rp>）</rp></ruby
      >のふりかえり
    </h1>
    <m-info-field label="今回（こんかい）の目標（もくひょう）" class="mb-6">
      {{ goal }}
    </m-info-field>
    <div class="flex flex-col w-full mb-6">
      <div class="leading-7 text-sm text-gray-600">
        今回（こんかい）の集中度（しゅうちゅうど）
      </div>
      <div class="p-3 rounded-md bg-white flex">
        <div
          v-for="button in concentrations"
          :key="`concentration${button.value}`"
          class="w-1/5 flex flex-col items-center cursor-pointer"
          @click="concentration = button.value"
        >
          <img :src="getIconFile(button.value)" alt="" class="w-1/2 mb-1" />
          <div
            class="text-xs"
            :class="
              button.value === concentration
                ? 'text-primary-500'
                : 'text-gray-400'
            "
          >
            {{ button.label }}
          </div>
        </div>
      </div>
    </div>
    <m-text-selector
      :choices="thoughtsChoices"
      :value="thoughts"
      class="mb-6"
      @change="onThoughtsChange"
    >
      <template #title>今日（きょう）の感想（かんそう）</template>
    </m-text-selector>
    <m-button :invalid="!validForm" class="mb-6" @click="send">おわる</m-button>
  </main>
</template>

<script lang="ts">
import { Options, Vue } from "vue-class-component";
import MCommentPanel from "@/components/MCommentPanel.vue";
import MButton from "@/components/form/MButton.vue";
import MInfoField from "@/components/form/MInfoField.vue";
import MTextArea from "@/components/form/MTextArea.vue";
import MTextField from "@/components/form/MTextField.vue";
import MSelectBox from "@/components/form/MSelectBox.vue";
import MTextSelector from "@/components/form/MTextSelector.vue";
import store, { awaitStudentLoaded } from "@/store";
import {
  Learning,
  learningCollectionKey,
  LearningReflection,
  LearningSubject
} from "@/entities/learning";
import { getLearning, setReflectionToLearning } from "@/api/learning";
import { sendMail } from "@/api/mail";
import { saveErrorLog } from "@/api/error";
import dayjs from "dayjs";

@Options({
  components: {
    MCommentPanel,
    MButton,
    MInfoField,
    MTextArea,
    MTextField,
    MSelectBox,
    MTextSelector
  }
})
export default class ReflectionForm extends Vue {
  learning: Learning | null = null;
  enteredAchievement = false;
  images: string[] = [];
  subjects: LearningSubject[] = [];
  concentration = 0;
  thoughts = "";
  thoughtsChoices = [
    "きょうはがんばった！",
    "もくひょうたっせい！",
    "たのしかった！",
    "もっとやりたい！",
    "ほめてほしい！",
    "つぎはがんばるぞ！"
  ];
  concentrations: {
    label: string;
    value: number;
  }[] = [
    {
      label: "ダメだった",
      value: 1
    },
    {
      label: "いまいち",
      value: 2
    },
    {
      label: "ふつう",
      value: 3
    },
    {
      label: "よかった！",
      value: 4
    },
    {
      label: "さいこう！",
      value: 5
    }
  ];

  get goal(): string {
    return this.learning?.data.goal ?? "";
  }

  get validConcentration(): boolean {
    return this.concentration > 0 && this.concentration <= 5;
  }

  get validForm() {
    return this.validConcentration && this.thoughts.length > 0;
  }

  getIconFile(value: number) {
    return require(`@/assets/concentration_${value}${
      value === this.concentration ? "_selected" : ""
    }.svg`);
  }

  allEnter() {
    this.enteredAchievement = true;
  }

  onThoughtsChange(next: string) {
    this.thoughts = next;
  }

  async send() {
    this.allEnter();

    if (!this.validForm || !this.learning) {
      return;
    }

    store.commit("SET_LOADING", true);
    const reflection: LearningReflection = {
      achievementRate: 5,
      additionalContent: "",
      images: [],
      concentration: this.concentration,
      complaint: 5,
      thoughts: this.thoughts,
      other: ""
    };

    try {
      store.commit("SET_LOAD_TEXT", "振り返り登録中...");
      await setReflectionToLearning(
        this.learning.ref,
        reflection,
        this.learning.data.subjects
      );
    } catch (e) {
      store.commit("SET_LOADING", false);
      store.commit("SET_LOAD_TEXT", "");
      alert(
        `振り返りの登録に失敗しました。\n時間を置いてから再度実行してみてください。\n\n${e}`
      );
      await saveErrorLog(
        store.state.student,
        e.code,
        e.message,
        "Failed to register reflection to learning"
      );
      return;
    }

    if (
      store.state.student?.data.emailReceivers?.length &&
      store.getters.involvedRoomReservations[0]
    ) {
      try {
        store.commit("SET_LOAD_TEXT", "メール送信中...");
        const studentName = store.state.student?.data.name;
        const thisLearningStartedOn = dayjs.unix(this.learning.data.timestamp);
        const [
          hh,
          mm
        ] = store.getters.involvedRoomReservations[0].data.time
          .split(":")
          .map((_: string) => +_);
        const now = dayjs();
        const nextLearningStartOn = now
          .clone()
          .hour(hh)
          .minute(mm)
          .add(
            now.isBefore(
              now
                .clone()
                .hour(hh)
                .minute(mm)
            )
              ? 0
              : 1,
            "day"
          );
        const nextTimeText = nextLearningStartOn.format("MM/DD HH:mm");
        const learningDetailURL = `${location.origin}/learning/${this.$route.params.learningId}?openExternalBrowser=1`;
        const subjectsText = this.learning.data.subjects
          .map(s => s.name)
          .join(",");
        const concentrationText =
          this.concentrations.find(c => c.value === this.concentration)
            ?.label ?? "";
        const html =
          thisLearningStartedOn.diff(now, "hour") >= -1
            ? `
          ${studentName}さんが退室しました！<br>ぜひ今日のがんばりを褒めてあげてください♪<br><br>---------------------<br><br>■ ${studentName}さんの入室前宣言<br>【意気込み】<br>${this.learning.data.goal}<br>【科目】<br>${subjectsText}<br><br>■ ${studentName}さんの今日のがんばり<br>【集中度】<br>${concentrationText}<br>【感想】<br>${this.thoughts}<br><br>■ 連続学習日数<br>${store.getters.consecutiveLearningDayCount} 日<br><br>▼ 学習の様子を記録した動画はこちらからダウンロードしていただけます。<br><a href="${learningDetailURL}">学習記録を開く</a><br><br>---------------------<br><br>次回は${nextTimeText}からです。<br>この調子で毎日の習慣化を頑張りましょう！`
            : `${studentName}さんが ${thisLearningStartedOn.format(
                "MM/DD"
              )} の学習の振り返りを記入しました！<br>ぜひがんばりを褒めてあげてください♪<br><br>---------------------<br><br>■ ${studentName}さんの入室前宣言<br>【意気込み】<br>${
                this.learning.data.goal
              }<br>【科目】<br>${subjectsText}<br><br>■ ${studentName}さんのがんばり<br>【集中度】<br>${concentrationText}<br>【感想】<br>${
                this.thoughts
              }<br><br>■ 連続学習日数<br>${
                store.getters.consecutiveLearningDayCount
              } 日<br><br>▼ 学習の様子を記録した動画はこちらからダウンロードしていただけます。<br><a href="${learningDetailURL}">学習記録を開く</a><br><br>---------------------<br><br>次回は${nextTimeText}からです。<br>この調子で毎日の習慣化を頑張りましょう！`;
        await sendMail({
          title: `【オンライン自習室】学習結果のお知らせ`,
          html,
          emailReceivers: store.state.student?.data.emailReceivers
        });
      } catch (e) {
        store.commit("SET_LOADING", false);
        store.commit("SET_LOAD_TEXT", "");
        alert(`メールの送信に失敗しました。\n\n${e}`);
        await saveErrorLog(
          store.state.student,
          e.code,
          e.message,
          "Failed to send mail on registering reflection"
        );
        return;
      }
    }

    store.commit("SET_LOADING", false);
    store.commit("SET_LOAD_TEXT", "");

    this.$router.replace({
      path: "/"
    });
  }

  async created() {
    const student = await awaitStudentLoaded(store);
    const learningId = this.$route.params.learningId as string;
    if (!learningId) {
      return;
    }

    try {
      const learning = await getLearning(
        student.ref.collection(learningCollectionKey).doc(learningId)
      );
      if (!learning) {
        alert("学習データが見つかりませんでした");
        this.$router.replace("/");
        return;
      }
      this.learning = learning;
      if (this.learning.data.reflection) {
        /**
         通知から遷移した場合など、記入済の場合は保存された値をセットする
         */
        this.concentration =
          this.learning.data.reflection.concentration || this.concentration;
        this.thoughts = this.learning.data.reflection.thoughts || this.thoughts;
        this.subjects = learning.data.subjects;
      }
    } catch (e) {
      alert(`学習データの取得に失敗しました\n\n${e}`);
      await saveErrorLog(
        store.state.student,
        e.code,
        e.message,
        "Failed to get learning data"
      );
    }
  }
}
</script>
